import React, { forwardRef } from "react";
import { TEMPLATE, FLOOR_BOX, QR_BOX } from "../../constants/brandedExport";

/**
 * BrandedExportFlyer — the fixed-canvas branded flyer.
 *
 * Pure presentational component. It is rendered BOTH as the on-screen preview
 * (scaled down via a CSS transform on a wrapper) and as the off-screen capture
 * target for dom-to-image — so the preview is a faithful miniature of the
 * downloaded file.
 *
 * Rather than drawing branding in code, it composites two rasters onto one of
 * the pre-designed template artboards: the floor plan is dropped into a fixed
 * box via object-fit: contain, and the QR into its fixed square. Because the
 * artwork (logo, wordmark, disclaimer, tagline) is baked into the SVG, there is
 * no external font/CORS surface for the capture to trip over.
 *
 * @param {Object} props
 * @param {string|null} props.templateSrc - data URL of the chosen template SVG.
 * @param {string|null} props.planImage - data URL of the captured floor plan.
 * @param {string|null} props.qrDataUrl - data URL of the QR (omitted on no-qr).
 * @param {Object} [props.style]
 */
const BrandedExportFlyer = forwardRef(function BrandedExportFlyer(
  { templateSrc, planImage, qrDataUrl = null, style },
  ref
) {
  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        width: TEMPLATE.WIDTH,
        height: TEMPLATE.HEIGHT,
        background: "#ffffff",
        overflow: "hidden",
        ...style,
      }}
    >
      {templateSrc && (
        <img
          src={templateSrc}
          alt=""
          width={TEMPLATE.WIDTH}
          height={TEMPLATE.HEIGHT}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: TEMPLATE.WIDTH,
            height: TEMPLATE.HEIGHT,
            display: "block",
          }}
        />
      )}

      {/* Floor plan drop zone */}
      <div
        style={{
          position: "absolute",
          left: FLOOR_BOX.x,
          top: FLOOR_BOX.y,
          width: FLOOR_BOX.width,
          height: FLOOR_BOX.height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          boxSizing: "border-box",
          padding: 8,
        }}
      >
        {planImage && (
          <img
            src={planImage}
            alt="Floor plan"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: "block",
            }}
          />
        )}
      </div>

      {/* QR drop zone (only rendered when a QR exists) */}
      {qrDataUrl && (
        <div
          style={{
            position: "absolute",
            left: QR_BOX.x,
            top: QR_BOX.y,
            width: QR_BOX.size,
            height: QR_BOX.size,
          }}
        >
          <img
            src={qrDataUrl}
            alt="Gallery QR code"
            style={{ width: "100%", height: "100%", display: "block" }}
          />
        </div>
      )}
    </div>
  );
});

export default BrandedExportFlyer;
